import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Utility } from 'helpers/UtilityHelpers';
import { Code } from 'constants/codes';
import apiLogin from "services/loginInterceptors";
import { Location } from 'models/LocationModel';
import ReCAPTCHA from 'react-google-recaptcha';
import { MediaList } from 'models/BaseModel';
import { Files } from 'helpers/FileHelpers';

import 'assets/styles/Intake.css';
import {
  type IntakeOnlineData,
  type IntakeFormErrors,
  type IntakeJsonErrors,
  type YesNoType,
  type YesNoNaType,
  CANADIAN_PROVINCES,
  FAMILY_EYE_CONDITIONS,
  GENDER_OPTIONS,
  INITIAL_FORM_STATE,
  MEDICAL_CONDITIONS,
  RELATIONSHIP_OPTIONS,
  VISION_SYMPTOMS,
  toggleArrayItem,
  validateForm,
  IntakeJsonData,
  INITIAL_JSON_STATE,
} from 'models/IntakeOnlineData';
import { Control } from 'components/common/ControlManager';

interface ThemeColor {
  header?: string;
  footer?: string;
  button?: string;
  text?: string;
  steps?: string;
  date?: string;
}

// ─── Shared sub-components ────────────────────────────────────────────────────

interface YesNoRadioProps {
  value: string;
  onChange: (v: string) => void;
  includeNA?: boolean;
  hasError?: boolean;
}
const YesNoRadio: React.FC<YesNoRadioProps> = ({ value, onChange, includeNA = false, hasError }) => {
  const opts = includeNA ? ['Yes', 'No', 'N/A'] : ['Yes', 'No'];
  return (
    <div className="radio-group">
      {opts.map(opt => {
        const isSelected = value === opt;
        const colorClass = isSelected
          ? opt === 'Yes' ? 'selected-yes' : 'selected-no'
          : hasError ? 'error-radio' : '';
        return (
          <div key={opt} className={`radio-option ${colorClass}`} onClick={() => onChange(opt)}>
            <div className="radio-dot">
              {isSelected && <div className="radio-dot-inner" />}
            </div>
            {opt}
          </div>
        );
      })}
    </div>
  );
};

interface CheckboxGroupProps {
  options: string[];
  selected: string[];
  onToggle: (item: string) => void;
}
const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ options, selected, onToggle }) => (
  <div className="checkbox-group">
    {options.map(opt => {
      const isChecked = selected.includes(opt);
      return (
        <div key={opt} className={`checkbox-option ${isChecked ? 'checked' : ''}`} onClick={() => onToggle(opt)}>
          <div className="custom-checkbox">
            {isChecked && (
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
          </div>
          {opt}
        </div>
      );
    })}
  </div>
);

interface ClinicHeaderProps { name: string; address: string; themeColor: string; logoUrl?: string | null; }
const ClinicHeader: React.FC<ClinicHeaderProps> = ({ name, address, themeColor, logoUrl }) => (
  <div className="clinic-header" style={{backgroundColor:`#${themeColor}`}}>
    <div className="clinic-logo">
      {logoUrl ? (
        <img src={logoUrl} alt="Clinic Logo" />
      ) : (
        <div className="clinic-name">{name}</div>
      )}
    </div>
    <div className="clinic-tagline">{address}</div>
  </div>
);

interface SuccessPanelProps { firstName: string; }
const SuccessPanel: React.FC<SuccessPanelProps> = ({ firstName }) => (
  <div className="success-panel">
    <div className="success-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
        stroke="#38a169" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </div>
    <h3>Intake Form Submitted!</h3>
    <p>
      Thank you, {firstName}. Your intake form has been received.
    </p>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const IntakeOnline: React.FC = () => {
  const { keyId: paramKeyId } = useParams<{ keyId: string }>();
  const [keyId, setKeyId]     = useState<string>(paramKeyId ?? '');
  const [toast, setToast] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<Number>(2);
  const [locationId, setLocationId] = useState<Number>(2);
  const [databaseId, setDatabaseId] = useState<Number>(2);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyAddress, setCompanyAddress] = useState<string>('');
  const [locationPath, setLocationPath] = useState<string>(keyId);
  const [insurers, setInsurers] = useState<{ value: number; label: string }[]>([]);
  const [relations, setRelations] = useState<{ value: number; label: string }[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null >(null);
  const showLog = false;
  
  const [showTermsOfUse, setShowTermsOfUse] = useState<boolean>(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState<boolean>(false);

  const [headerThemeColor, setHeaderThemeColor] = useState<string>('0d3b5e');
  const [footerThemeColor, setFooterThemeColor] = useState<string>('666');
  const [buttonThemeColor, setButtonThemeColor] = useState<string>('0d3b5e');
  const [textThemeColor, setTextThemeColor] = useState<string>('008716');

  const [textSubmitButton, setTextSubmitButton] = useState<string>('Submit Intake Form');
  const [enableSubmitButton, setEnableSubmitButton] = useState<boolean>(true);
  
  // const companyId   = 12;
  // const locationId  = 14;
  // const clinicName    = 'Dr. Azenda Optometry (Walmart Vision)';
  // const clinicAddress = '#3800 Memorial Drive NE, Calgary, AB T2A 2K2';

  const [form, setForm] = useState<IntakeOnlineData>(INITIAL_FORM_STATE);
  const [jsonData, setJsonData] = useState<IntakeJsonData>(INITIAL_JSON_STATE);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<IntakeFormErrors>({});
  const [errJson, setErrJson] = useState<IntakeJsonErrors>({});
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const yearOptions = Array.from({ length: 2026 - 1920 + 1 }, (_, i) => (2026 - i).toString());
  const monthOptions = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
  const dayOptions = Array.from({ length: 31 }, (_, i) => (i + 1).toString());

  const [dobYear, setDobYear] = useState<string>('');
  const [dobMonth, setDobMonth] = useState<string>('');
  const [dobDay, setDobDay] = useState<string>('');

  const update = <K extends keyof IntakeOnlineData>(key: K, value: IntakeOnlineData[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const jsonUpdate = <K extends keyof IntakeJsonData>(key: K, value: IntakeJsonData[K]) =>
    setJsonData(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      intake_dob: dobYear && dobMonth && dobDay
        ? `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')} 10:00:00`
        : ''
    }));
  }, [dobYear, dobMonth, dobDay]);


  useEffect(() => {
    showLog && console.log("form----->",form);
  }, [form]);

  const toggleCheck = (
    key: 'D25' | 'D26' | 'D29',
    item: string,
  ) => setJsonData(prev => ({ ...prev, [key]: toggleArrayItem(prev[key], item) }));

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
  ev.preventDefault();
  const [validationErrors,validationJson] = validateForm(form, jsonData);
  setErrors(validationErrors);
  setErrJson(validationJson);

  if (Object.keys(validationErrors).length > 0 || Object.keys(validationJson).length > 0) {
    setToast('Please fill in all required fields before submitting.');
    setTimeout(() => setToast(null), 4000);
    return;
  }

  if (!recaptchaToken) {
    setToast('Please complete the reCAPTCHA verification.');
    setTimeout(() => setToast(null), 4000);
    return;
  }

  setTextSubmitButton('Submitting...');
  setEnableSubmitButton(false);

  try {
    const response = await fetch(`${Code.Path.SERVER}intake/online`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        ...form,
        company_id: companyId,
        location_id: locationId,
        intake_date: Utility.Date.getLocalDate(),
        intake_data: JSON.stringify(jsonData, (key, value) => {
          if (value === null || value === undefined || value === "") return undefined;
          if (value === false || value === 0) return undefined;
          if (Array.isArray(value) && value.length === 0) return undefined;
          if (Array.isArray(value)) return value.join(", ");
          if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return undefined;
          return value;
        }),
        keyId 
      }),
    });

    // if (!response.ok) {
    //   const errorData = await response.json().catch(() => null);
    //   showLog && console.error('Submission failed:', response.status, errorData);
    //   // Optionally surface an error to the user here
    //   return;
    // }

    setSubmitted(true);
  } catch (err) {
    showLog && console.error('Network error during submission:', err);
    // Optionally surface a network error to the user here
  }
};

 useEffect(() => {
  const fetchLocation = async () => {
    if (locationPath) {
      try {
        const url = `${Code.Path.SERVER}auth/location/${locationPath}`;
        const response = await apiLogin.get(url);
        showLog && console.clear();
     
        if (response) {
          const result = response.data;
     
          if (result.status === "success" && result.code === 200) {
            const location = result.data as Location;
            if (location) {
              setCompanyId(location.company_id || 0);
              setLocationId(location.location_id || 0);
              setDatabaseId(location.database_id || 0);
              setCompanyName(location.company_legal_name || '');
              setCompanyAddress(location.location_address?.replace(/\r?\n/g, "") || '');

              showLog && console.log("Location:", location);

              jsonUpdate('company_name', location.company_legal_name || '');
              jsonUpdate('location_name', location.location_name || '')

              const themeColor = location.theme_color;
              if (themeColor) {

          
                const colors: ThemeColor = JSON.parse(themeColor);
          
                showLog && console.log("Theme colors:", colors);

                setHeaderThemeColor(colors.header || '0d3b5e');
                setFooterThemeColor(colors.footer || '666');
                setButtonThemeColor(colors.button || '0d3b5e');
                setTextThemeColor(colors.text || '008716');
              }

              const fileList = location.file_list;


              document.title = `Patient Intake Form - ${location.company_legal_name} - Powered by NextVizon`;
              let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
              if (!metaDesc) {
                metaDesc = document.createElement("meta");
                metaDesc.name = "description";
                document.head.appendChild(metaDesc);
              }
              metaDesc.setAttribute("content", `It is highly recommended to complete your patient intake form before your appointment to skip paperwork at ${location.company_legal_name}. Save time and enjoy a smoother visit by securely providing your personal, medical, and vision information online in advance, helping us serve you better during your appointment.`);
              
              let metaRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
              if (!metaRobots) {
                metaRobots = document.createElement("meta");
                metaRobots.name = "robots";
                document.head.appendChild(metaRobots);
              }
              metaRobots.setAttribute("content", "index, follow");  


              showLog && console.log("Location fileList:", fileList);
              
              if (fileList && location.company_path) {
                const fnames: MediaList = JSON.parse(fileList);
                const imageName = fnames.logo;
                const companyPath = location.company_path;
                showLog && console.log("Location company_path:", companyPath);
                showLog && console.log("Location imageName:", imageName);

                if(imageName){
                  const logo = await Files.ImageAPI.FetchWT(companyPath, imageName);
                  showLog && console.log("Fetched logo URL:", logo);
                  setLogoUrl(logo);
                }
              }
            }
          }
        }
      } catch (error) {
        showLog && console.error("fetchLocation error >>>>>", error);
        setCompanyId(11); //demo xyz optiical
        setLocationId(13); //demo xyz optiical
        setDatabaseId(10); //demo xyz optiical
        setCompanyName(Utility.Convert.TitleCase(locationPath?.replaceAll("-"," ") || ''));
        setCompanyAddress('');

        
      }
    }
  };

  fetchLocation();
}, [locationPath]);


  useEffect(() => {
    if (paramKeyId) setKeyId(paramKeyId);
  }, [paramKeyId]);

   useEffect(() => {
    if (databaseId){ 
      jsonUpdate('database_id', databaseId);
    }
  }, [databaseId]);

  // Fetch insurers from JSON
  useEffect(() => {
    fetch('/data/common/insurer.json')
      .then(res => res.json())
      .then(data => setInsurers(data))
      .catch(err => console.error('Failed to load insurers:', err));
  }, []);

  useEffect(() => {
    fetch('/data/common/relation.json')
      .then(res => res.json())
      .then(data => setRelations(data))
      .catch(err => console.error('Failed to load insurers:', err));
  }, []);

  return (
    <div className="page-wrapper">
      <ClinicHeader name={companyName} address={companyAddress} themeColor={headerThemeColor} logoUrl={logoUrl} />
      {/* <ClinicHeader name={companyName} address={companyAddress} /> */}

      <div className="form-card">
        <div className="form-title-bar" style={{backgroundColor:`#${footerThemeColor}`}}>
          <h2>Patient Intake Form</h2>
          <span className="badge">Confidential</span>
        </div>

        <div className="form-body">
          {submitted ? (
            <SuccessPanel firstName={form.intake_first_name} />
          ) : (
            <form onSubmit={handleSubmit} noValidate>

              {/* ══ 1. Personal Information ══════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Personal Information</div>

                {/* Smoker */}
                <div className="field mb-14">
                  <label>Are you an existing patient? <span className="clinic-req">*</span></label>
                  <YesNoRadio value={jsonData.D79}
                    onChange={v => jsonUpdate('D79', v as YesNoType)}
                    hasError={!!errJson.D79}
                  />
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>First Name <span className="clinic-req">*</span></label>
                    <input className={errors.intake_first_name ? 'error' : ''} placeholder="Jane"
                      value={form.intake_first_name} onChange={e => update('intake_first_name', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Last Name <span className="clinic-req">*</span></label>
                    <input className={errors.intake_last_name ? 'error' : ''} placeholder="Smith"
                      value={form.intake_last_name} onChange={e => update('intake_last_name', e.target.value)} />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Date of Birth <span className="clinic-req">*</span></label>
                    <div className={`dob-selects ${errors.intake_dob ? 'error' : ''}`}>
                      <div className="grid-3">
                        <div className="field">
                          <select value={dobYear} onChange={e => setDobYear(e.target.value)}>
                            <option value="">Year</option>
                            {yearOptions.map(year => <option key={year} value={year}>{year}</option>)}
                          </select>
                        </div>
                        <div className="field">
                          <select value={dobMonth} onChange={e => setDobMonth(e.target.value)}>
                            <option value="">Month</option>
                            {monthOptions.map(month => <option key={month} value={month}>{month}</option>)}
                          </select>
                        </div>
                        <div className="field">
                          <select value={dobDay} onChange={e => setDobDay(e.target.value)}>
                            <option value="">Day</option>
                            {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="field">
                    <label>Mobile Number <span className="clinic-req">*</span></label>
                    <input type="tel" className={errors.intake_phone ? 'error' : ''} placeholder="555-000-0000"
                      value={form.intake_phone} onChange={e => update('intake_phone', e.target.value)} />
                  </div>
                </div>

                <div className="field mb-14">
                  <label>Email Address</label>
                  <input type="email" placeholder="jane.smith@email.com"
                    value={form.intake_email} onChange={e => update('intake_email', e.target.value)} />
                </div>

                {/* Gender */}
                <div className="field">
                  <label>Gender</label>
                  <div className="gender-group">
                    {GENDER_OPTIONS.map(opt => (
                      <div
                        key={opt}
                        className={`gender-pill ${form.intake_gender === opt ? 'selected' : ''}`}
                        onClick={() => update('intake_gender', form.intake_gender === opt ? '' : opt)}
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ══ 2. Health Card ═══════════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Health Card</div>
                <div className="field">
                  <label>Health Card Number</label>
                  <input placeholder="e.g. 123456789"
                    value={form.intake_health_card} onChange={e => update('intake_health_card', e.target.value)} />
                </div>
              </div>

              {/* ══ 3. Home Address ══════════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Home Address</div>

                <div className="field mb-14">
                  <label>Street Address</label>
                  <input placeholder="123 Maple Street, Apt 4B"
                    value={form.intake_address} onChange={e => update('intake_address', e.target.value)} />
                </div>

                <div className="grid-3">
                  <div className="field col-span-2">
                    <label>City</label>
                    <input placeholder="Calgary"
                      value={form.intake_city} onChange={e => update('intake_city', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Province</label>
                    <select value={form.intake_province} onChange={e => update('intake_province', e.target.value)}>
                      <option value="">—</option>
                      {CANADIAN_PROVINCES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                <div className="field mt-14 mw-160">
                  <label>Postal Code</label>
                  <input placeholder="T2A 1B3"
                    value={form.intake_postal} onChange={e => update('intake_postal', e.target.value)} />
                </div>
              </div>

              {/* ══ 4. Health Insurance ══════════════════════════════════════════ */}
              <div className="section section--emergency">
                <div className="section-label">Health Insurance</div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Health Insurer Name</label>
                    <select
                      value={form.insurer_id}
                      onChange={e => update('insurer_id', Number(e.target.value))}
                    >
                      <option value="2">Select Insurer Name</option>
                      {insurers.map(insurer => (
                        <option key={insurer.value} value={insurer.value}>
                          {insurer.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label>Member Number</label>
                    <input placeholder="MBR-000000"
                      value={jsonData.D84} onChange={e => jsonUpdate('D84', e.target.value)} />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Policy Number</label>
                    <input placeholder="PLC-000000"
                      value={jsonData.D86} onChange={e => jsonUpdate('D86', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Group Number</label>
                    <input placeholder="GRP-000000"
                      value={jsonData.D87} onChange={e => jsonUpdate('D87', e.target.value)} />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                    <div className="field">
                      <label>Relationship to the policy holder</label>
                      <select
                        value={form.primary_relationship_id}
                        onChange={e => update('primary_relationship_id', Number(e.target.value))}
                      >
                        <option value="2">Select Relation</option>
                        {relations.map(relation => (
                          <option key={relation.value} value={relation.value}>
                            {relation.label}
                          </option>
                        ))}
                      </select>
                      {/* <select value={jsonData.D88}
                        onChange={e => jsonUpdate('D88', e.target.value as IntakeJsonData['D88'])}>
                        <option value="">Select…</option>
                        {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select> */}
                    </div>
                </div>
                
              </div>


              {/* ══ 5. Visit Information ═════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Visit Information</div>

                <div className="field mb-14">
                  <label>Reason for Today's Visit</label>
                  <textarea placeholder="e.g. Routine eye exam, blurry vision, contact lens fitting…"
                    value={jsonData.D28} onChange={e => jsonUpdate('D28', e.target.value)} />
                </div>
              </div>
                

              {/* ══ 6. Current Vision Symptoms ═══════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Current Vision Symptoms</div>
                <p style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>Select all that apply:</p>
                <CheckboxGroup
                  options={VISION_SYMPTOMS}
                  selected={jsonData.D29}
                  onToggle={item => toggleCheck('D29', item)}
                />
              </div>

              {/* ══ 7. Medical History ═══════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Medical History</div>

                {/* Diabetes */}
                <div className="field mb-14">
                  <label>Are you diabetic?</label>
                  <YesNoRadio value={jsonData.D63}
                    onChange={v => jsonUpdate('D63', v as YesNoType)}
                   />
                  {jsonData.D63 === 'Yes' && (
                    <div className="detail-box">
                      <div className="detail-row">
                        <div>
                          <label>Most Recent Blood Sugar (mmol/L)</label>
                          <input placeholder="e.g. 5.6"
                            value={jsonData.D35}
                            onChange={e => jsonUpdate('D35', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Pregnancy */}
                <div className="field mb-14">
                  <label>Are you currently pregnant?</label>
                  <YesNoRadio value={jsonData.D62}
                    onChange={v => jsonUpdate('D62', v as YesNoNaType)}
                    includeNA />
                </div>

                {/* Smoker */}
                <div className="field mb-14">
                  <label>Do you smoke?</label>
                  <YesNoRadio value={jsonData.D33}
                    onChange={v => jsonUpdate('D33', v as YesNoType)}
                  />
                </div>

                {/* Eye Surgery */}
                <div className="field mb-14">
                  <label>Have you had any previous eye surgery?</label>
                  <YesNoRadio value={jsonData.hasD24}
                    onChange={v => jsonUpdate('hasD24', v as YesNoType)}
                  />
                  {jsonData.hasD24 === 'Yes' && (
                    <div className="detail-box">
                      <label>Please describe the surgery (type, date, eye affected)</label>
                      <textarea placeholder="e.g. LASIK on both eyes, 2019…"
                        value={jsonData.D24}
                        onChange={e => jsonUpdate('D24', e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Eye Injury */}
                <div className="field mb-14">
                  <label>Have you had any eye injury or trauma?</label>
                  <YesNoRadio value={jsonData.hasD36}
                    onChange={v => jsonUpdate('hasD36', v as YesNoType)}
                  />
                  {jsonData.hasD36 === 'Yes' && (
                    <div className="detail-box">
                      <label>Please describe the injury</label>
                      <textarea placeholder="e.g. Foreign object in eye, 2021…"
                        value={jsonData.D36}
                        onChange={e => jsonUpdate('D36', e.target.value)} />
                    </div>
                  )}
                </div>

                {/* Current Medications */}
                <div className="field mb-14">
                  <label>Current Medications</label>
                  <textarea placeholder="List all prescription and over-the-counter medications, including eye drops…"
                    value={jsonData.D30}
                    onChange={e => jsonUpdate('D30', e.target.value)} />
                </div>

                {/* Allergies */}
                <div className="field mb-14">
                  <label>Known Allergies</label>
                  <input placeholder="e.g. Penicillin, latex, eye drops…"
                    value={jsonData.D31}
                    onChange={e => jsonUpdate('D31', e.target.value)} />
                </div>

                {/* Medical Conditions */}
                <div className="field">
                  <label>
                    Do you have any of the following conditions?{' '}
                    <span style={{ color: '#a0aec0', fontWeight: 400 }}>(select all that apply)</span>
                  </label>
                  <div className="mb-8" />
                  <CheckboxGroup
                    options={MEDICAL_CONDITIONS}
                    selected={jsonData.D25}
                    onToggle={item => toggleCheck('D25', item)}
                  />
             </div>
              </div>

              {/* ══ 8. Family Eye History ════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Family Eye History</div>
                <p style={{ fontSize: 12, color: '#718096', marginBottom: 10 }}>
                  Has anyone in your immediate family been diagnosed with:
                </p>
                <CheckboxGroup
                  options={FAMILY_EYE_CONDITIONS}
                  selected={jsonData.D26}
                  onToggle={item => toggleCheck('D26', item)}
                />
              </div>

              {/* ══ 9. Family Physician ═════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Family Physician</div>
                <div className="grid-2">
                  <div className="field">
                    <label>Family Doctor's Name</label>
                    <input placeholder="Dr. Name"
                      value={jsonData.B77}
                      onChange={e => jsonUpdate('B77', e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Doctor's Phone Number</label>
                    <input type="tel" placeholder="000-000-0000"
                      value={jsonData.B78}
                      onChange={e => jsonUpdate('B78', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* ══ 10. Emergency Contact ═════════════════════════════════════════ */}
              <div className="section">
                <div className="section-label">Emergency Contact</div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Full Name</label>
                    <input
                      placeholder="John Smith"
                      value={jsonData.B74}
                      onChange={e => jsonUpdate('B74', e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Relationship to Patient</label>
                    <input
                      placeholder="e.g. Spouse, Parent, Sibling"
                      value={jsonData.B75}
                      onChange={e => jsonUpdate('B75', e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid-2 mb-14">
                  <div className="field">
                    <label>Primary Phone</label>
                    <input
                      type="tel"
                      placeholder="555-000-0000"
                      value={jsonData.B76}
                      onChange={e => jsonUpdate('B76', e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Alternate Phone</label>
                    <input
                      type="tel"
                      placeholder="555-000-0000"
                      value={jsonData.B94}
                      onChange={e => jsonUpdate('B94', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* ══ 11. Terms & Consent ══════════════════════════════════════════ */}
              <div
                className={['terms-block', form.terms ? 'checked' : '', errors.terms ? 'error-border' : ''].join(' ')}
                onClick={() => update('terms', !form.terms)}
              >
                <div className="custom-check">
                  {form.terms && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div className="terms-text">
                  <div>
                    I hereby consent to the disclosure of my eye care patient records by my health care
                    provider to {companyName} for the purpose of fulfilling its obligation with respect to
                    secretarial and administrative support services, including management of patient files,
                    scheduling eye care appointments, issuing reminder notices, telephone answering, fee
                    collection (if applicable), and processing provincial health insurance fee claims (if
                    applicable), in accordance with the <a onClick={() => setShowTermsOfUse(showTermsOfUse ? false : true)}>Terms of Use</a> and <a onClick={() => setShowPrivacyPolicy(showPrivacyPolicy ? false : true)}>Privacy Policy</a>.  <span className="clinic-req">*</span>
                  </div>
                  {showTermsOfUse && (
                    <Control.Form.Terms />
                  )}
                  {showPrivacyPolicy && (
                    <Control.Form.Privacy />
                  )}
                </div>
              </div>

              <div className="recaptcha-container" style={{ marginTop: '16px', marginBottom: '16px' }}>
                <ReCAPTCHA
                  sitekey="6Lc8uG4qAAAAAHCmxZUJINB2c2wH13iuBCWOwr5k" // Replace with your actual Google reCAPTCHA site key
                  onChange={setRecaptchaToken}
                />
              </div>

              {/* ══ Submit ═══════════════════════════════════════════════════════ */}
              <button type="submit" className="submit-btn" disabled={!enableSubmitButton} style={{backgroundColor:`#${buttonThemeColor}`}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 2L11 13" /><path d="M22 2L15 22 11 13 2 9l20-7z" />
                </svg>
                {textSubmitButton}
              </button>

              <div className="privacy-notice">
                🔒 Your information is encrypted and protected under HIPAA / PHIPA.<br />
                This form is for new and returning patients of {companyName}.
              </div>
            </form>
          )}
        </div>
      </div>
      {toast && (
        <div className="toast-error">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {toast}
        </div>
      )}
    </div>
  );
};

export default IntakeOnline;